import { z } from 'zod';

import { VENUE_SLOT_MAX_CAPACITY, VenueSlotSchema } from '../venue-slot/venue-slot';

/**
 * The most slots one request may open.
 *
 * A hard ceiling on the **shape**, under which `meetings.maxSlotsPerBatch` is
 * the deployment's own dial (U9). Two numbers rather than one because they
 * answer different questions: this one says what a body may contain at all, and
 * refusing it is `validation_failed`; the setting says what this deployment is
 * willing to write in one act, and refusing that is a typed
 * `venue_slot_batch_too_large` an operator can be given a sentence about.
 */
export const VENUE_SLOT_BATCH_MAX = 500;

/**
 * One slot inside a batch.
 *
 * No `status`: a generated slot is **offered**, or there would be no reason to
 * generate it. Closing one is a later, deliberate act on a row that exists.
 */
export const VenueSlotBatchEntrySchema = z
  .object({
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    capacity: z.number().int().min(1).max(VENUE_SLOT_MAX_CAPACITY),
  })
  .strict()
  .refine((slot) => Date.parse(slot.endsAt) > Date.parse(slot.startsAt), {
    message: 'A slot must end after it starts.',
    path: ['endsAt'],
  });

export type VenueSlotBatchEntry = z.infer<typeof VenueSlotBatchEntrySchema>;

/**
 * Opening many slots at once — the recurring-availability generator's request.
 *
 * ## It carries the EXPANDED slots, never the rule that produced them
 *
 * "Every Sunday, 15:00–17:00, for the next eight weeks" is a wall-clock
 * recurrence, and a wall clock means nothing without a zone. Sending the rule
 * would make the api resolve it in `DEFAULT_TIMEZONE` while every other admin
 * instant is read in the operator's browser zone (`DateTimeInput`) — one feature
 * with two zone stories, and a daylight-saving weekend where they disagree. So
 * the dialog expands the rule with `local-instant.util` (which already resolves
 * both DST edges, and is spec'd across four zones), **shows the operator every
 * slot it is about to create**, and posts the instants. The rule is never stored
 * and never re-interpreted, so there is nothing to get wrong twice.
 *
 * ## `skipOverlapping` has no default
 *
 * "Create what fits and pass over the rest" and "refuse the whole batch" are
 * materially different acts against a partner's calendar. A default would pick
 * one of them on the operator's behalf, on the surface where they are least
 * likely to notice — so the dialog asks, and the contract requires the answer.
 */
export const VenueSlotBatchSchema = z
  .object({
    slots: z.array(VenueSlotBatchEntrySchema).min(1).max(VENUE_SLOT_BATCH_MAX),
    skipOverlapping: z.boolean(),
  })
  .strict();

export type VenueSlotBatchRequest = z.infer<typeof VenueSlotBatchSchema>;

/**
 * What the batch actually did.
 *
 * `skipped` is not bookkeeping: an operator who asked for twenty-four slots and
 * got nineteen must be told so **on the surface that asked**, not left to
 * discover it by counting a grid. It is the same argument `maxPhotos` travels on
 * the venue detail for — a control that stops without saying why has stopped
 * explaining itself (U0).
 */
export const VenueSlotBatchResultSchema = z
  .object({
    created: z.array(VenueSlotSchema),
    skipped: z.number().int().nonnegative(),
  })
  .strict();

export type VenueSlotBatchResult = z.infer<typeof VenueSlotBatchResultSchema>;
